import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Card, CardHeader, CardBody } from "shards-react";

import VChart from "./VChart";

const CURRENCIES = ['IRR', 'EUR', 'USD'];
const CURRENCY_COLORS = {
  IRR: '#007bff',
  EUR: '#8445f7',
  USD: '#fb7906',
};

const formatAmount = value => Number(value || 0).toLocaleString();

const formatRange = (from, exclusiveTo) => {
  if (!from || !exclusiveTo) return '';

  const toDate = new Date(new Date(exclusiveTo).getTime() - (24 * 60 * 60 * 1000));
  const formatDate = value => new Date(value).toLocaleDateString(undefined, {
    timeZone: 'UTC',
  });
  return `${formatDate(from)} - ${formatDate(toDate)}`;
};

const chartOptions = {
  responsive: true,
  legend: {
    position: "top",
  },
  scales: {
    xAxes: [
      {
        gridLines: false,
        ticks: {
          autoSkip: true,
          maxRotation: 45,
          maxTicksLimit: 12,
          minRotation: 0,
        },
      }
    ],
    yAxes: [
      {
        id: 'amount',
        position: 'left',
        ticks: {
          beginAtZero: true,
          callback(value) {
            if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
            if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
            return value;
          },
        },
      },
      {
        id: 'count',
        position: 'right',
        gridLines: {
          drawOnChartArea: false,
        },
        ticks: {
          beginAtZero: true,
          precision: 0,
        },
      }
    ],
  },
  hover: {
    mode: "nearest",
    intersect: false,
  },
  tooltips: {
    custom: false,
    mode: "index",
    intersect: false,
  },
};

const PaymentOverview = (props) => {
  const { labels, datasets } = useMemo(() => {
    const series = Array.isArray(props.series) ? props.series : [];
    const activeCurrencies = CURRENCIES.filter(currency =>
      series.some(item => Number(item.totalsByCurrency?.[currency] || 0) !== 0)
    );
    const currencyDatasets = activeCurrencies.map((currency, index) => {
      const values = series.map(item =>
        Number(item.totalsByCurrency?.[currency] || 0)
      );
      const total = values.reduce((sum, value) => sum + value, 0);
      return {
        label: `${currency} Amount (${formatAmount(total)})`,
        data: values,
        order: index + 1,
        type: 'line',
        yAxisID: 'amount',
        color: CURRENCY_COLORS[currency],
      };
    });
    const counts = series.map(item => Number(item.count || 0));

    return {
      labels: series.map(item => item.period),
      datasets: [
        ...currencyDatasets,
        {
          label: `Payment Count (${counts.reduce((sum, count) => sum + count, 0)})`,
          data: counts,
          order: currencyDatasets.length + 1,
          type: 'line',
          yAxisID: 'count',
          color: '#17c671',
        },
      ],
    };
  }, [props.series]);
  const hasData = props.series.some(item => (
    Number(item.count || 0) > 0
    || CURRENCIES.some(
      currency => Number(item.totalsByCurrency?.[currency] || 0) !== 0
    )
  ));

  return (
    <Card small className="h-100">
      <CardHeader className="border-bottom">
        <h6 className="m-0">{props.title}</h6>
        {props.meta.fallbackUsed && (
          <small className="text-warning d-block">
            {`No recent payments; showing latest available data (${formatRange(
              props.meta.displayedFrom,
              props.meta.displayedTo,
            )})`}
          </small>
        )}
      </CardHeader>
      <CardBody className="pt-0">
        {hasData ? (
          <VChart
            labels={labels}
            datasets={datasets}
            type="bar"
            height="115"
            chartOptions={chartOptions}
          />
        ) : (
          <div className="d-flex align-items-center justify-content-center text-muted h-100 py-5">
            No successful payments in the selected range.
          </div>
        )}
      </CardBody>
    </Card>
  );
};

PaymentOverview.propTypes = {
  meta: PropTypes.shape({
    displayedFrom: PropTypes.string,
    displayedTo: PropTypes.string,
    fallbackUsed: PropTypes.bool,
  }),
  title: PropTypes.string,
  series: PropTypes.array,
};

PaymentOverview.defaultProps = {
  meta: {},
  title: "Payments by period and currency",
  series: [],
};

export default PaymentOverview;
